import React, { PropsWithChildren, ReactNode, useState } from 'react';
import { Box, Button, Flex, InlineLink, Text } from '@fullstory/components';
import { ContactPaneId, PlanContactSidecar, ReasonId } from '@fullstory/overlays-ui';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import { assertExhaustive } from '@fullstory/typeutils';
import { generateRoutePath, PLAN_UPGRADE_ROUTE } from '@fullstory/routes';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';
import css from './index.module.scss';
import { classNames } from '@fullstory/util/fmt';

interface FeatureWedgeUpsellProps {
  variant:
    | 'journeys'
    | 'conversions'
    | 'retention'
    | 'watchedElements'
    | 'conversionMaps'
    | 'dashboards';
}

interface FeatureWedgeContent {
  title: string;
  description: string;
  disclaimer: string | ReactNode;
}

const { getOrgId } = fullstorySelectors;

export const FeatureWedgeUpsell: React.FunctionComponent<FeatureWedgeUpsellProps> = ({
  variant,
}) => {
  const [sidecarOpen, setSidecarOpen] = useState(false);
  const orgId = useSelector(getOrgId);
  const ComparePlansLink: React.FC<PropsWithChildren<unknown>> = ({ children }) => (
    <InlineLink to={generateRoutePath(PLAN_UPGRADE_ROUTE, { orgId })}>{children}</InlineLink>
  );

  let featureWedgeContent: FeatureWedgeContent = {
    title: '',
    description: '',
    disclaimer: '',
  };

  switch (variant) {
    case 'journeys':
      featureWedgeContent = {
        title: intl.formatMessage({
          defaultMessage: 'How are your customers using your site?',
          id: 'mtI8C0',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Ready to find out? Journeys help you visualize how users explore your site, showing you step-by-step page navigation and key interactions along the way.',
          id: 'p4RrLC',
        }),
        disclaimer: intl.formatMessage(
          {
            defaultMessage:
              'Journeys aren’t included in your current plan. <comparePlansLink>Compare plans</comparePlansLink> and learn how to add them.',
            id: 'OQX6zt',
          },
          { comparePlansLink: (text: ReactNode) => <ComparePlansLink>{text}</ComparePlansLink> },
        ),
      };
      break;
    case 'conversions':
      featureWedgeContent = {
        title: intl.formatMessage({
          defaultMessage: 'Why aren’t your users converting?',
          id: 'zqX/r6',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Ready to find out?  Conversions helps you discover, understand, and quickly fix the areas where your users get off track.',
          id: '9T1xQ9',
        }),
        disclaimer: intl.formatMessage(
          {
            defaultMessage:
              'The Conversions feature is not included in your current plan. <comparePlansLink>Compare plans</comparePlansLink> and learn how to add them.',
            id: '4bwdge',
          },
          { comparePlansLink: (text: ReactNode) => <ComparePlansLink>{text}</ComparePlansLink> },
        ),
      };
      break;
    case 'conversionMaps':
      featureWedgeContent = {
        title: intl.formatMessage({
          defaultMessage: 'How many users convert from this page?',
          id: '0k9b4a',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Ready to find out?  Our conversion maps show you where your users are most engaged.',
          id: 'kr0rkH',
        }),
        disclaimer: intl.formatMessage(
          {
            defaultMessage: `Conversion maps aren't included in your current plan. <comparePlansLink>Compare plans</comparePlansLink> and learn how to add them.`,
            id: 'AZqs+P',
          },
          { comparePlansLink: (text: ReactNode) => <ComparePlansLink>{text}</ComparePlansLink> },
        ),
      };
      break;
    case 'retention':
      featureWedgeContent = {
        title: intl.formatMessage({
          defaultMessage: 'Do users return to your site again and again?',
          id: 'U9Ok2q',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Ready to find out?  Retention helps you understand when users find enough value in your digital experience to come back again and again.',
          id: 'RYR8T1',
        }),
        disclaimer: intl.formatMessage(
          {
            defaultMessage:
              'Retention analysis isn’t included in your current plan. <comparePlansLink>Compare plans</comparePlansLink> and learn how to add it.',
            id: 'TJ4y5l',
          },
          { comparePlansLink: (text: ReactNode) => <ComparePlansLink>{text}</ComparePlansLink> },
        ),
      };
      break;
    case 'watchedElements':
      featureWedgeContent = {
        title: intl.formatMessage({
          defaultMessage: 'How many users saw that error message?',
          id: '4hpjuO',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Ready to find out?  Watched Elements let you determine if your users saw an error message, promo banner, or any CSS element that was rendered on the page - even if they didn’t engage with it.',
          id: 'BPOwfE',
        }),
        disclaimer: intl.formatMessage(
          {
            defaultMessage:
              'Watched Elements aren’t included in your current plan. <comparePlansLink>Compare plans</comparePlansLink> and learn how to add it.',
            id: 'UkNMSo',
          },
          { comparePlansLink: (text: ReactNode) => <ComparePlansLink>{text}</ComparePlansLink> },
        ),
      };
      break;
    case 'dashboards':
      featureWedgeContent = {
        title: intl.formatMessage({
          defaultMessage: 'Upgrade to dashboards',
          id: 'M28LWa',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Dashboards are customized visualizations using your Fullstory data. Keep your relevant analytics organized and prioritized.',
          id: '72rpm3',
        }),
        disclaimer: intl.formatMessage(
          {
            defaultMessage:
              'Dashboards are not included in your current plan. <comparePlansLink>Compare plans</comparePlansLink> and learn how to add them.',
            id: 'FVjiJH',
          },
          {
            comparePlansLink: (text: ReactNode) => <ComparePlansLink>{text}</ComparePlansLink>,
          },
        ),
      };
      break;
    default:
      assertExhaustive(variant);
  }

  return (
    <Flex testId="feature-wedge-upsell" className={css.featureWedgeUpsell} column>
      <Box marginTop={48} marginBottom={16}>
        <Text appearance="heading1" align="center">
          {featureWedgeContent.title}
        </Text>
      </Box>
      <Box maxWidth={792} marginBottom={24}>
        <Text appearance="bodyMedium" color="secondary" align="center">
          {featureWedgeContent.description}
        </Text>
      </Box>

      <Box
        marginBottom={8}
        width={1}
        maxWidth={840}
        className={classNames(css[variant], css.common)}
        style={{ aspectRatio: '35/19', backgroundSize: 'cover' }}
      />

      <Box marginBottom={8}>
        <Text appearance="bodySmall" align="center">
          {featureWedgeContent.disclaimer}
        </Text>
      </Box>
      <Box paddingTop={16} marginBottom={24}>
        <Button
          size="medium"
          variant="fill-primary"
          onClick={() => setSidecarOpen(true)}
          justifyContent="center"
        >
          <FormattedMessage defaultMessage="Contact Us" id="hZOGLS" />
        </Button>
      </Box>

      <PlanContactSidecar
        isUpgradePage
        initialReasonId={ReasonId.UpgradePlan}
        initialPane={ContactPaneId.ContactUs}
        closeFn={() => setSidecarOpen(false)}
        open={sidecarOpen}
      />
    </Flex>
  );
};
